import { useState } from 'react';

export default function ShareButtons({
  title,
  description,
  url,
  hashtags = ['experiments', 'innovation', 'strategy']
}) {
  const [copied, setCopied] = useState(false);
  const baseUrl = 'https://deedslab.com';
  const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;

  const shareUrls = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(fullUrl)}&hashtags=${hashtags.join(',')}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(fullUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fullUrl)}`,
    reddit: `https://reddit.com/submit?url=${encodeURIComponent(fullUrl)}&title=${encodeURIComponent(title)}`
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const openShareWindow = (url) => {
    window.open(url, '_blank', 'width=600,height=400');
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-muted-dark mr-2">Share:</span>

      {/* Twitter */}
      <button
        onClick={() => openShareWindow(shareUrls.twitter)}
        data-share-platform="twitter"
        className="inline-flex items-center justify-center w-8 h-8 border border-border-dark bg-surface-dark-1 hover:bg-surface-dark-2 text-text-dark rounded-full transition-colors"
        aria-label="Share on Twitter"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
        </svg>
      </button>

      {/* LinkedIn */}
      <button
        onClick={() => openShareWindow(shareUrls.linkedin)}
        data-share-platform="linkedin"
        className="inline-flex items-center justify-center w-8 h-8 border border-border-dark bg-surface-dark-1 hover:bg-surface-dark-2 text-text-dark rounded-full transition-colors"
        aria-label="Share on LinkedIn"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      </button>

      {/* Facebook */}
      <button
        onClick={() => openShareWindow(shareUrls.facebook)}
        data-share-platform="facebook"
        className="inline-flex items-center justify-center w-8 h-8 border border-border-dark bg-surface-dark-1 hover:bg-surface-dark-2 text-text-dark rounded-full transition-colors"
        aria-label="Share on Facebook"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      </button>

      {/* Reddit */}
      <button
        onClick={() => openShareWindow(shareUrls.reddit)}
        data-share-platform="reddit"
        className="inline-flex items-center justify-center w-8 h-8 border border-border-dark bg-surface-dark-1 hover:bg-surface-dark-2 text-text-dark rounded-full transition-colors"
        aria-label="Share on Reddit"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.283-.56.773-1.488 1.498-1.488 1.03 0 1.75.968 1.75 1.998 0 1.39-1.018 2.728-2.728 3.15-.77.195-1.56.32-2.35.32-.79 0-1.58-.125-2.35-.32-1.71-.422-2.728-1.76-2.728-3.15 0-1.03.72-1.998 1.75-1.998.725 0 1.215.928 1.498 1.488 1.194-.856 2.85-1.418 4.674-1.488l-.8-3.747-2.597.547a1.25 1.25 0 0 1 .252 2.443zm-2.49 4.99c-.563 0-1.018.453-1.018 1.012 0 .559.455 1.012 1.018 1.012.562 0 1.017-.453 1.017-1.012 0-.559-.455-1.012-1.017-1.012zm5.49 0c-.562 0-1.017.453-1.017 1.012 0 .559.455 1.012 1.017 1.012.563 0 1.018-.453 1.018-1.012 0-.559-.455-1.012-1.018-1.012zM12 10.02c-1.87 0-3.39 1.52-3.39 3.39 0 1.87 1.52 3.39 3.39 3.39s3.39-1.52 3.39-3.39c0-1.87-1.52-3.39-3.39-3.39zm0 4.78c-.85 0-1.54-.69-1.54-1.54s.69-1.54 1.54-1.54 1.54.69 1.54 1.54-.69 1.54-1.54 1.54z"/>
        </svg>
      </button>

      {/* Copy Link */}
      <button
        onClick={copyToClipboard}
        data-copy-link
        className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
          copied
            ? 'bg-green-600 text-white'
            : 'bg-surface-dark-1 hover:bg-surface-dark-2 text-text-dark'
        }`}
        aria-label={copied ? "Link copied!" : "Copy link"}
      >
        {copied ? (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        )}
      </button>
    </div>
  );
}
